/**
 * attivazione — cosa succede quando un'azienda accende uno strumento a canone (atlante cap. 251).
 *
 * Una sola regola, usata dal bottone della vetrina, da Yuma e dal server, così il gestionale dice
 * sempre la stessa cosa che poi accade:
 *  - azienda gratuita per decisione (`billing.exempt`) o dimostrativa (`billing.natura`) → si accende, niente addebito;
 *  - abbonamento in corso (con la carta) → lo strumento entra nell'abbonamento, e durante la prova
 *    dell'abbonamento è gratis fino alla sua fine;
 *  - prova gratuita senza carta ancora in corso → si accende gratis fino alla fine della prova;
 *  - pagamento non andato a buon fine, o abbonamento in pausa → non si accende, e si dice perché;
 *  - altrimenti → serve la pagina di pagamento.
 *
 * Nucleo PURO, condiviso fra gestionale e server.
 */
import { naturaDellAzienda } from '../abbonamento/naturaAzienda';

/** Ciò che serve sapere di un'azienda, letto dal suo documento con `aziendaPerGliStrumenti`. */
export interface AziendaPerGliStrumenti {
  /**
   * Non paga: `billing.exempt` (azienda vera che non paga per decisione: Vivisolar, Power Service, Energit, Futurnova)
   * oppure azienda dimostrativa (`naturaDellAzienda`, 02/11/2026: Impianti Demo, Azienda Demo YumaOS, Test
   * collaborazione FF). Per tutte e due niente addebiti, niente promemoria della prova, niente ritorno al piano gratuito.
   */
  readonly gratuita: boolean;
  readonly abbonamento_id: string | null;
  /** Lo stato Stripe dell'abbonamento (`active`, `trialing`, `past_due`, …). */
  readonly stato_abbonamento: string | null;
  /** Fine della prova gratuita, in millisecondi; null = nessuna prova. */
  readonly fine_prova_ms: number | null;
}

export type ViaDiAttivazione =
  | { readonly via: 'senza_addebito' }
  | { readonly via: 'nell_abbonamento'; readonly gratis_fino_al_ms: number | null }
  | { readonly via: 'gratis_fino_a_fine_prova'; readonly fino_al_ms: number }
  | { readonly via: 'pagamento_in_sospeso' }
  | { readonly via: 'abbonamento_in_pausa' }
  | { readonly via: 'serve_il_pagamento' };

/** Stati Stripe in cui l'abbonamento è vivo e gli si possono aggiungere righe. */
const STATI_VIVI = new Set(['active', 'trialing']);
/** Stati in cui un pagamento è rimasto indietro: prima si paga, poi si accende altro. */
const STATI_IN_SOSPESO = new Set(['past_due', 'unpaid']);

export function viaDiAttivazione(azienda: AziendaPerGliStrumenti, oraMs: number): ViaDiAttivazione {
  if (azienda.gratuita) return { via: 'senza_addebito' };
  const stato = azienda.stato_abbonamento ?? '';
  if (azienda.abbonamento_id) {
    if (STATI_VIVI.has(stato)) {
      const inProva = stato === 'trialing' && azienda.fine_prova_ms !== null && azienda.fine_prova_ms > oraMs;
      return { via: 'nell_abbonamento', gratis_fino_al_ms: inProva ? azienda.fine_prova_ms : null };
    }
    if (STATI_IN_SOSPESO.has(stato)) return { via: 'pagamento_in_sospeso' };
    if (stato === 'paused') return { via: 'abbonamento_in_pausa' };
  }
  if (azienda.fine_prova_ms !== null && azienda.fine_prova_ms > oraMs) {
    return { via: 'gratis_fino_a_fine_prova', fino_al_ms: azienda.fine_prova_ms };
  }
  return { via: 'serve_il_pagamento' };
}

/**
 * La prova gratuita in corso in cui cade una via di attivazione, o null se si paga da subito (o non si paga per
 * decisione). Vale per la prova senza carta e per quella dell'abbonamento con la carta.
 */
export function fineProvaDellaVia(via: ViaDiAttivazione): number | null {
  if (via.via === 'gratis_fino_a_fine_prova') return via.fino_al_ms;
  if (via.via === 'nell_abbonamento') return via.gratis_fino_al_ms;
  return null;
}

/**
 * Uno strumento che NON si attiva in prova (`attivabile_in_prova: false`, «Gestione delle campagne» dal 28/10/2026)
 * dentro una prova in corso: il motivo, con la data in cui finisce la prova. Null = nessun ostacolo da qui (gli altri
 * «no», pagamento indietro o pausa, li dice `viaDiAttivazione`). Stessa regola nella scheda, in Yuma e sul server.
 */
export function bloccatoDallaProva(
  strumento: { readonly attivabile_in_prova?: boolean },
  via: ViaDiAttivazione,
): { readonly fine_prova_ms: number } | null {
  if (strumento.attivabile_in_prova !== false) return null;
  const fine = fineProvaDellaVia(via);
  return fine === null ? null : { fine_prova_ms: fine };
}

/**
 * L'azienda ha un abbonamento Stripe vivo (con la carta): la fine della prova e gli addebiti li
 * gestisce Stripe, non il controllo notturno delle prove senza carta. Anche un abbonamento con un
 * pagamento indietro o in pausa è vivo: lo segue il ciclo dei solleciti, non la scadenza della prova.
 */
export function haUnAbbonamentoVivo(azienda: AziendaPerGliStrumenti): boolean {
  if (!azienda.abbonamento_id) return false;
  const stato = azienda.stato_abbonamento ?? '';
  return STATI_VIVI.has(stato) || STATI_IN_SOSPESO.has(stato) || stato === 'paused';
}

/** Una data che può arrivare come Timestamp di Firestore, `Date`, millisecondi o `{ _seconds }` (callable). */
export function millisDa(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (v instanceof Date) return Number.isFinite(v.getTime()) ? v.getTime() : null;
  if (typeof v === 'object') {
    const o = v as { toMillis?: unknown; seconds?: unknown; _seconds?: unknown };
    if (typeof o.toMillis === 'function') return (o.toMillis as () => number)();
    const sec = typeof o.seconds === 'number' ? o.seconds : typeof o._seconds === 'number' ? o._seconds : null;
    if (sec !== null) return sec * 1000;
  }
  return null;
}

/**
 * Dal documento `tenants/{id}`. La fine prova si legge prima in cima al documento (prova senza carta,
 * `dailyTrialExpirationCheck`), poi in `billing` (prova dell'abbonamento Stripe, scritta dal webhook).
 */
export function aziendaPerGliStrumenti(datiAzienda: unknown): AziendaPerGliStrumenti {
  const d = (typeof datiAzienda === 'object' && datiAzienda !== null ? datiAzienda : {}) as Record<string, unknown>;
  const b = (typeof d.billing === 'object' && d.billing !== null ? d.billing : {}) as Record<string, unknown>;
  const id = typeof b.stripe_subscription_id === 'string' && b.stripe_subscription_id !== '' ? b.stripe_subscription_id : null;
  return {
    gratuita: b.exempt === true || naturaDellAzienda(d) === 'dimostrativa',
    abbonamento_id: id,
    stato_abbonamento: typeof b.subscription_status === 'string' ? b.subscription_status : null,
    fine_prova_ms: millisDa(d.trial_ends_at) ?? millisDa(b.trial_ends_at),
  };
}
